import React, { useEffect, useState, Fragment } from 'react';
import { Observer } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Typography,
  Button,
  MenuItem,
  Menu,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { css } from '@emotion/core';
import InvalidIcon from '@material-ui/icons/Warning';
import { useRootStore } from '../../../stores/BridgeProvider';
import { safeEval } from '../../util/UtilFunctions';
import { CustomTable, FilterPlaceholder } from '../../common/CustomTable';
import { COLORS } from '../../util/Colors';
import { NumberHandler } from '../../util/NumberHandler';
import DealTranTableForm from './DealTranTableForm';

const DealTransactions = ({ DealId, refreshDeal }) => {
  const { authStore, dataStore } = useRootStore();
  const { state } = useLocation();
  const history = useHistory();
  const { contactArray, transactionCategoryArray } = dataStore;
  const [isLoading, setLoading] = useState(true);
  const [dealTransactions, setDealTransactions] = useState([]);
  const [isEditing, setEditing] = useState(false);
  const contacts = dataStore.contactArray;
  const accounts = dataStore.validAccountArray;
  const transactionCategories = dataStore.validTransactionCategoryArray;

  const getDealTransactions = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/deal-transactions/${DealId}`,
      );
      setDealTransactions(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const requests = [getDealTransactions()];
      if (contacts.length === 0) {
        requests.push(dataStore.retrieveContacts());
      }
      if (transactionCategories.length === 0) {
        requests.push(dataStore.retrieveTransactionCategories());
      }
      if (accounts.length === 0) {
        requests.push(dataStore.retrieveAccounts());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);

  const wipeChanges = () => {
    getDealTransactions();
    setEditing(false);
  };

  const newTransaction = (clone = {}) => {
    const dtsClone = [...dealTransactions];
    dtsClone.push({
      DealId,
      TranType: clone.TranType || '',
      CategoryId: clone.CategoryId || '',
      ContactId: clone.ContactId || '',
      Price: clone.Price || '',
      Quantity: clone.Quantity || 1,
      Description: clone.Description || '',
      DebitAccount: clone.DebitAccount || '',
      CreditAccount: clone.CreditAccount || '',
      dirty: true,
    });
    setDealTransactions(dtsClone);
  };

  useEffect(() => {
    const { dealTranEditMode } = state || {};
    if (!isLoading && dealTranEditMode) {
      setEditing(true);
      newTransaction();
    }
  }, [isLoading]);

  if (isLoading) return null;

  const deleteDealTransaction = async cellInfo => {
    const dealTransaction = dealTransactions[cellInfo.index];
    const { DealTranId } = dealTransaction;
    if (DealTranId) {
      try {
        await authStore.request.delete(
          `${process.env.REACT_APP_API}/api/deal-transaction`,
          {
            data: { DealTransactionId: DealTranId },
          },
        );
      } catch (e) {
        console.error(e);
      }
    }
    const dtsClone = [...dealTransactions];
    dtsClone.splice(cellInfo.index, 1);
    setDealTransactions(dtsClone);
  };

  const DealTranDropdown = ({ cellInfo }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const dealTransaction = dealTransactions[cellInfo.index];

    const open = Boolean(anchorEl);

    const handleClick = event => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <Fragment>
        <IconButton
          onClick={e => handleClick(e)}
          style={{ padding: 0, height: 'fit-content' }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={() => deleteDealTransaction(cellInfo)}>
            Delete Transaction
          </MenuItem>
          <MenuItem onClick={() => newTransaction(dealTransaction)}>
            Clone Transaction
          </MenuItem>
        </Menu>
      </Fragment>
    );
  };

  const ValidationErrors = ({ cellInfo }) => {
    const dealTransaction = dealTransactions[cellInfo.index];
    const { errors } = dealTransaction;

    if (!errors) return null;

    return (
      <Tooltip
        title={
          <ul style={{ padding: 0, listStyleType: 'none' }}>
            {errors.map(error => (
              <li key={error}>* {error}</li>
            ))}
          </ul>
        }
      >
        <InvalidIcon
          style={{
            color: COLORS.red,
          }}
        />
      </Tooltip>
    );
  };

  const editDetailsButton = () => {
    setEditing(true);
    if (dealTransactions && dealTransactions.length === 0) {
      newTransaction();
    }
  };

  return (
    <Observer
      render={() => {
        const columns = [
          {
            Header: () => null,
            width: 30,
            Cell: ({ row }) => <ValidationErrors cellInfo={row} />,
            id: 1,
            show: isEditing,
          },
          {
            Header: 'AP/AR',
            id: 2,
            accessor: 'TranType',
            Cell: ({ cell }) => {
              return cell.value;
            },
            width: 60,
          },
          {
            Header: 'Category',
            Cell: ({ cell }) => {
              return cell.value;
            },
            accessor: d =>
              safeEval(
                () =>
                  transactionCategoryArray.find(
                    e => e.TranCategoryId === d.CategoryId,
                  ).Code,
                'N/A',
              ),
            id: 3,
            Filter: FilterPlaceholder,
          },

          {
            Header: 'Price',

            width: 70,
            Cell: ({ row }) => (
              <NumberHandler
                num={row.original.Price}
                currency="$"
                justify="flex-end"
                float={2}
              />
            ),

            accessor: d => (d.Amount ? d.Amount : 'N/A'),
            id: 4,
          },

          {
            Header: 'Quantity',
            // maxWidth: 100,
            width: 60,
            Cell: ({ cell }) => {
              return cell.value;
            },
            accessor: d => (d.Quantity ? parseInt(d.Quantity, 10) : 'N/A'),
            id: 5,
          },
          {
            Header: 'Total',
            // maxWidth: 120,
            width: isEditing ? 70 : 60,
            // FIXME: Price - make sure these are numbers!
            // Also currently 0.3333 x 3 = 1.00 which is bad
            Cell: ({ row }) => (
              <NumberHandler
                num={row.original.Quantity * row.original.Price}
                currency="$"
                justify="flex-end"
                float={2}
              />
            ),

            accessor: d => d.Amount || 'N/A',
            id: 6,
          },

          {
            Header: 'To',
            // width: 200,
            width: 150,
            Cell: ({ cell }) => {
              return cell.value;
            },
            accessor: d =>
              safeEval(
                () => contactArray.find(e => e.ContactId === d.ContactId).Name,
                'N/A',
              ),
            id: 7,
          },
          {
            Header: '',
            width: 30,
            Cell: ({ row }) => <DealTranDropdown cellInfo={row} />,
            id: 8,
            show: isEditing,
          },
        ];

        return (
          <>
            {!isEditing ? (
              <CustomTable
                data={dealTransactions || []}
                columns={columns}
                minRows={1}
                disableQueryParams
                trClassName="react-table-hover-row"
                tableHeader={<Typography variant="h4">Transactions</Typography>}
                actionables={
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={editDetailsButton}
                  >
                    Edit Details
                  </Button>
                }
                getTrProps={({ row }) => ({
                  onClick: isEditing
                    ? null
                    : () =>
                        history.push(
                          `/deal-transaction/${row.original.DealTranId}/edit`,
                        ),
                })}
                css={
                  isEditing &&
                  css`
                    .rt-td {
                      overflow: inherit;
                      //TODO: conditional
                      padding: 0px 5px;
                      white-space: inherit;
                    }
                    .rt-tr {
                      padding: 0px 0px;
                    }
                  `
                }
              />
            ) : (
              <div>
                <DealTranTableForm
                  initialVal={dealTransactions}
                  DealId={DealId}
                  wipeChanges={wipeChanges}
                  setDealTransactions={setDealTransactions}
                  refreshDeal={refreshDeal}
                />
              </div>
            )}
          </>
        );
      }}
    />
  );
};

export default DealTransactions;
